import React from "react";
import ReactDOM from "react-dom";
import { Provider as ReduxProvider } from "react-redux";

import { initFFClient } from "lib/featureFlags";
import getStore from "redux/store";

import { RootProvider } from "common/RootContext";
import I18nProvider from "apps/providers/i18nProvider";
import MUIProvider from "apps/providers/MUIProvider";

initFFClient();

function init(
    el,
    ContainerComponent,
    renderedComponent,
    externalMap,
    _store,
    state,
    componentType,
) {
    const store = getStore(_store, state);

    ReactDOM.render(
        <ReduxProvider store={store}>
            <I18nProvider>
                <RootProvider value={el}>
                    <MUIProvider snackbar>
                        <ContainerComponent componentType={componentType}>
                            {renderedComponent}
                        </ContainerComponent>
                    </MUIProvider>
                </RootProvider>
            </I18nProvider>
        </ReduxProvider>,
        el,
    );

    Object.keys(externalMap).forEach((k) => {
        const extComponent = externalMap[k];
        const eel = document.getElementById(k);
        if (!eel) {
            return;
        }

        ReactDOM.render(
            <ReduxProvider store={store}>
                <I18nProvider>
                    <RootProvider value={eel}>
                        <MUIProvider>{extComponent}</MUIProvider>
                    </RootProvider>
                </I18nProvider>
            </ReduxProvider>,
            eel,
        );
    });

    return store;
}

export default function createGenericHandler(
    ContainerComponent,
    COMPONENT_MAP,
    componentType,
    externalMap = {},
) {
    return (name, options = {}) => {
        const data = COMPONENT_MAP[name];
        if (!data) {
            throw new Error(`Invalid name for init: ${name}`);
        }

        const { Component, getInitialState = (x) => x, store = null } = data;

        const {
            el = document.getElementById("root"),
            skipExternal = false,
            props = {},
            state = {},
        } = options;
        let wiredMap = {};

        // do not run external mappings for nav component, etc
        if (!skipExternal) {
            try {
                wiredMap = Object.keys(externalMap).reduce((a, c) => {
                    const Comp = externalMap[c];
                    const loadedComponent = <Comp {...props} />;
                    return { ...a, [c]: loadedComponent };
                }, {});
            } catch (e) {
                wiredMap = {};
            }
        }

        init(
            el,
            ContainerComponent,
            <Component {...props} />,
            wiredMap,
            store,
            getInitialState(state),
            componentType,
        );
        return () => ReactDOM.unmountComponentAtNode(el);
    };
}
